// Session titles come from the user's own live sessions and are rendered as text
// only, never injected as HTML.

import { isNumber, isString } from "../utils/schemaUtils";

export interface LoadedLiveSession {
  id: string;
  title: string;
  url?: string;
}

export interface LiveSessionNotFound {
  id: string;
  reason: string;
}

export interface LiveSessionLoadPayload {
  type: "live_session_load_results";
  version: number;
  sessions: LoadedLiveSession[];
  notFound: LiveSessionNotFound[];
  userError?: string;
}

const isNotFound = (v: unknown): v is LiveSessionNotFound => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.id) && isString(r.reason);
};

// The backend marshals empty slices as null rather than []. Treat missing/null
// as an empty list; a malformed (non-array) value fails the parse.
const asList = <T>(v: unknown, guard: (x: unknown) => x is T): T[] | null => {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || !v.every(guard)) return null;
  return v;
};

const isSession = (v: unknown): v is LoadedLiveSession => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.id) && isString(r.title) && (r.url === undefined || isString(r.url));
};

export const parseLiveSessionLoadPayload = (v: unknown): LiveSessionLoadPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "live_session_load_results") return null;
  if (!isNumber(r.version)) return null;
  const sessions = asList(r.sessions, isSession);
  const notFound = asList(r.notFound, isNotFound);
  if (sessions === null || notFound === null) return null;
  if (r.userError !== undefined && !isString(r.userError)) return null;
  return {
    type: "live_session_load_results",
    version: r.version,
    sessions,
    notFound,
    userError: r.userError,
  };
};
